import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { ExpandLess, ExpandMore, LocationOn, Stars } from '@mui/icons-material';

import { HotelAmentityDTO } from '@bandido/shared/dtos';
import { InfoLanguage } from '@bandido/shared/enums';

import { DescriptionEditorCategory } from '@frontend/features/hotel-content';
import { AmenityItem } from '@frontend/features/hotel-content/components/Detail/AmenityItem';
import { toProperName } from '@frontend/features/common/helper';
import Rating from '@frontend/features/hotel-content/components/Detail/Rating';


interface Props {
  name: string,
  categoryId: number,
  address: string,
  city: string,
  country: string,
  amenities: HotelAmentityDTO[],
  rating?: number | null
  totalReviews?: number | null
}

export const HotelFaresViewDetail: FC<Props> = ({ name, categoryId, address, city, country, amenities, rating, totalReviews  }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const [showAllAmenitites, setShowAllAmenities] = useState(false);

  const handleToggleShowAllAmenitites = () => {
    setShowAllAmenities((prev) => !prev);
  };

  const amenititesToShow = useMemo(() => {
    if (showAllAmenitites) {
      return amenities;
    } else {
      return amenities?.slice(0, 6);
    }
  }, [amenities, showAllAmenitites]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={theme.spacing(4)}
    >
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography
          variant="h6"
          color={theme.palette.text.primary}
        >
          {toProperName(name)}
        </Typography>

        <Rating rating={rating} totalReviews={totalReviews} />

        <Box display="flex" flexDirection="column" gap={1}>
          <Box gap={1} display="flex" alignItems="center">
            <Stars htmlColor={theme.palette.text.disabled} />
            <Typography variant="caption" color={theme.palette.text.secondary}>
              <DescriptionEditorCategory
                categoryId={categoryId}
                lang={i18n.language as InfoLanguage}
                onlyText
              />
            </Typography>
          </Box>
          <Box gap={1} display="flex" alignItems="center">
            <LocationOn htmlColor={theme.palette.text.disabled} />
            <Typography variant="caption" color={theme.palette.text.secondary} noWrap>
              {`${address}, ${city}, ${country}`}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        gap={theme.spacing(3)}
      >
        <Box>
          <Typography variant="h6" color={theme.palette.text.primary}>
            {t('hotels.fares.amenitites')}
          </Typography>
        </Box>
        <Box display="flex" flexWrap="wrap" gap={2} maxWidth="100%">
          {amenititesToShow.map((amenity) => (
            <Box
              key={amenity.id}
              width={400}
            >
              <AmenityItem amenity={amenity} />
            </Box>
          ))}
        </Box>
        <Box>
          {amenities.length > 6 && (
            <Button
              variant="text"
              onClick={handleToggleShowAllAmenitites}
            >
              {t(`hotels.fares.showAllAmenities${showAllAmenitites ? 'False' : 'True'}`)}
              {showAllAmenitites ? <ExpandLess /> : <ExpandMore />}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};
